import { useState, useEffect, useCallback } from "react";
import SubmitSkeleton from "../SubmitSkeleton";
import { useCaseContext } from "../../../context/CaseContext";
import { USStates } from "../resources";
import { capitalizeFirstLetters } from "../../../utils/stringUtils";
import Select, { StylesConfig } from "react-select";
import CustomLabel from "../../common/CustomLabel";
import Papa from "papaparse";
import devLogger from "../../../utils/devLogger";

interface CityData {
  city: string;
  city_ascii: string;
  state_id: string;
  state_name: string;
  county_fips: number;
  county_name: string;
  lat: number;
  lng: number;
  population: number;
  density: number;
  source: string;
  military: boolean;
  incorporated: boolean;
  timezone: string;
  ranking: number;
  zips: string;
  id: number;
}

const DetailedIncidentLocationStage = ({
  setStep,
}: {
  setStep: (step: number) => void;
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [citiesData, setCitiesData] = useState<CityData[]>([]);
  const { detailedIncidentLocation, updateCaseData } = useCaseContext();

  // Load cities data from CSV
  useEffect(() => {
    const loadCitiesData = async () => {
      try {
        setLoading(true);

        // Read the CSV file from resources
        const response = await fetch("/uscities.csv");
        const csvText = await response.text();

        // Parse the CSV data
        Papa.parse(csvText, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          complete: (results) => {
            setCitiesData(results.data as CityData[]);
            setLoading(false);
          },
          error: (error: Error) => {
            devLogger.error("Failed to parse CSV:", error);
            setErrorMessage("Failed to load location data. Please try again.");
            setLoading(false);
          },
        });
      } catch (error) {
        devLogger.error("Failed to load cities data:", error);
        setErrorMessage("Failed to load location data. Please try again.");
        setLoading(false);
      }
    };

    loadCitiesData();
  }, []);

  // Create select options based on available data
  const getStateOptions = useCallback(() => {
    return Object.keys(USStates).map((state) => ({
      value: state,
      label: capitalizeFirstLetters(state),
    }));
  }, []);

  const getCountyOptions = useCallback(() => {
    if (!detailedIncidentLocation.state || !citiesData.length) {
      return [];
    }

    // Get all counties in the selected state
    const stateCities = citiesData.filter(
      (city) =>
        city.state_name?.toLowerCase() ===
        detailedIncidentLocation.state.toLowerCase()
    );

    // Extract unique counties
    const uniqueCounties = Array.from(
      new Set(stateCities.map((city) => city.county_name))
    )
      .filter(Boolean)
      .sort();

    // Convert to select options format
    return uniqueCounties.map((county) => ({
      value: county.toLowerCase(),
      label: capitalizeFirstLetters(county),
    }));
  }, [citiesData, detailedIncidentLocation.state]);

  const getCityOptions = useCallback(() => {
    if (!detailedIncidentLocation.state || !citiesData.length) {
      return [];
    }

    // Filter cities by state
    let filteredCities = citiesData.filter(
      (city) =>
        city.state_name?.toLowerCase() ===
        detailedIncidentLocation.state.toLowerCase()
    );

    // If county is selected, further filter by county
    if (detailedIncidentLocation.county) {
      filteredCities = filteredCities.filter(
        (city) =>
          city.county_name?.toLowerCase() ===
          detailedIncidentLocation.county.toLowerCase()
      );
    }

    // Extract unique cities (some cities might appear multiple times with different zip codes)
    const uniqueCities = Array.from(
      new Map(
        filteredCities.map((city) => [city.city_ascii?.toLowerCase(), city])
      ).values()
    ).sort((a, b) => (a.city_ascii > b.city_ascii ? 1 : -1));

    // Convert to select options format
    return uniqueCities.map((city) => ({
      value: city.city_ascii?.toLowerCase(),
      label: capitalizeFirstLetters(city.city_ascii),
    }));
  }, [
    citiesData,
    detailedIncidentLocation.state,
    detailedIncidentLocation.county,
  ]);

  // Handle location change for any field
  const handleLocationChange = (val: string | undefined, field: string) => {
    if (!val) return;

    // Create updated location data
    let updatedLocation = { ...detailedIncidentLocation };

    // Handle clearing dependent fields when parent field changes
    if (field === "state") {
      // When state changes, clear county and city
      updatedLocation = {
        ...updatedLocation,
        state: val,
        county: "",
        city: "",
      };
    } else if (field === "county") {
      // When county changes, clear city but keep state
      updatedLocation = {
        ...updatedLocation,
        county: val,
        city: "",
      };
    } else if (field === "city") {
      // Find the county for this city
      const cityData = citiesData.find(
        (city) =>
          city.city_ascii?.toLowerCase() === val.toLowerCase() &&
          city.state_name?.toLowerCase() ===
            detailedIncidentLocation.state.toLowerCase()
      );

      if (cityData && cityData.county_name) {
        updatedLocation = {
          ...updatedLocation,
          city: val,
          county: cityData.county_name.toLowerCase(),
        };
      } else {
        updatedLocation = {
          ...updatedLocation,
          city: val,
        };
      }
    } else {
      // For other fields, just update normally
      updatedLocation = {
        ...updatedLocation,
        [field]: val,
      };
    }

    // Update the location data in context
    updateCaseData("detailedIncidentLocation", updatedLocation);
    setErrorMessage("");
  };

  const canProceed = () => {
    const { city, state } = detailedIncidentLocation;

    return state && city && state.length > 0 && city.length > 0;
  };

  const handleNext = () => {
    if (!canProceed()) {
      setErrorMessage("Please select state and city before proceeding.");
      return;
    }
    setErrorMessage("");
    setStep(5);
  };

  // Select styles
  const colourStyles: StylesConfig<any> = {
    control: (styles) => ({ ...styles, backgroundColor: "white" }),
    option: (styles, { isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
          ? "#000"
          : isFocused
          ? "#bcbcbc"
          : undefined,
        color: isDisabled
          ? "#ccc"
          : isSelected || isFocused
          ? "white"
          : "#131313",
        cursor: isDisabled ? "not-allowed" : "default",

        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? "white"
              : "white"
            : undefined,
        },
      };
    },
    input: (styles) => ({ ...styles }),
    placeholder: (styles) => ({ ...styles }),
    singleValue: (styles) => ({ ...styles }),
  };

  return (
    <SubmitSkeleton
      title="Location of the incident"
      onPrev={() => setStep(3)}
      onNext={handleNext}
      canProceed={!!canProceed()}
      progress={4 / 10}
    >
      <div className="flex flex-col gap-4">
        {errorMessage && (
          <p className="text-red-500 text-sm mb-1">{errorMessage}</p>
        )}

        {loading && (
          <p className="text-gray-7 text-sm">Loading location data...</p>
        )}

        {/* State Selection */}
        <CustomLabel labelText="State" isRequired>
          <Select
            components={{
              IndicatorSeparator: () => null,
            }}
            placeholder="Select State"
            className="font-medium text-[14px] leading-4 cursor-pointer"
            value={
              detailedIncidentLocation.state
                ? {
                    value: detailedIncidentLocation.state.toLowerCase(),
                    label: capitalizeFirstLetters(
                      detailedIncidentLocation.state
                    ),
                  }
                : null
            }
            onChange={(selectedOption) =>
              handleLocationChange(selectedOption?.value, "state")
            }
            styles={colourStyles}
            options={getStateOptions()}
            isDisabled={loading}
          />
        </CustomLabel>

        {/* County Selection */}
        <CustomLabel labelText="County (Optional)">
          <Select
            isLoading={loading}
            components={{
              IndicatorSeparator: () => null,
            }}
            placeholder="Select County"
            className="font-medium text-[14px] leading-4 cursor-pointer"
            value={
              detailedIncidentLocation.county
                ? {
                    value: detailedIncidentLocation.county.toLowerCase(),
                    label: capitalizeFirstLetters(
                      detailedIncidentLocation.county
                    ),
                  }
                : null
            }
            onChange={(selectedOption) =>
              handleLocationChange(selectedOption?.value, "county")
            }
            styles={colourStyles}
            options={getCountyOptions()}
            isDisabled={!detailedIncidentLocation.state || loading}
          />
          <p className="text-xs text-gray-4 mt-1">
            Selecting a county will filter the city list
          </p>
        </CustomLabel>

        {/* City Selection */}
        <CustomLabel labelText="City" isRequired>
          <Select
            isLoading={loading}
            components={{
              IndicatorSeparator: () => null,
            }}
            placeholder="Select City"
            className="font-medium text-[14px] leading-4 cursor-pointer"
            value={
              detailedIncidentLocation.city
                ? {
                    value: detailedIncidentLocation.city.toLowerCase(),
                    label: capitalizeFirstLetters(
                      detailedIncidentLocation.city
                    ),
                  }
                : null
            }
            options={getCityOptions()}
            styles={colourStyles}
            onChange={(selectedOption) =>
              handleLocationChange(selectedOption?.value, "city")
            }
            isDisabled={!detailedIncidentLocation.state || loading}
          />
          <p className="text-xs text-gray-4 mt-1">
            Select a city and the county will be auto-filled if available
          </p>
        </CustomLabel>
      </div>
    </SubmitSkeleton>
  );
};

export default DetailedIncidentLocationStage;
